import { useState, useEffect } from 'react';
import { X, Loader2, Plus, MapPin, AlertTriangle, Calendar, Clock } from 'lucide-react';
import { getCategoryIcon, API_BASE_URL } from '../../lib/utils';
import { useAppSelector } from '../../store';
import { useActiveEvent } from '../../contexts/EventContext';
import { useSystemConfig } from '../../contexts/SystemConfigContext';
import MapLocationPicker from '../map/MapLocationPicker';
import { formatEventLocationLabel, isValidGeoLocation } from '../../lib/eventLocation';
import type { Incident, IncidentSeverity, IncidentCategory } from '../../types';


interface CreateIncidentModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (incident: Incident) => void;
}

export default function CreateIncidentModal({ open, onClose, onCreated }: CreateIncidentModalProps) {
  const { token } = useAppSelector((state) => state.auth);
  const { activeEventId, activeEvent } = useActiveEvent();
  const { enabledIncidentCategories, enabledIncidentSeverities } = useSystemConfig();
  const severityOptions = enabledIncidentSeverities.map((s) => s.id as IncidentSeverity);
  const categoryOptions = enabledIncidentCategories.map((c) => c.id as IncidentCategory);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<IncidentCategory>(categoryOptions[0] || 'other');
  const [severity, setSeverity] = useState<IncidentSeverity>(severityOptions[0] || 'medium');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [showMapPicker, setShowMapPicker] = useState(false);

  const seedLocationFromEvent = () => {
    if (!isValidGeoLocation(activeEvent?.location)) return;
    const { latitude, longitude, address: eventAddress } = activeEvent!.location;
    setLatitude(latitude);
    setLongitude(longitude);
    setAddress(eventAddress?.trim() || formatEventLocationLabel(activeEvent));
  };

  useEffect(() => {
    if (!open) return;
    seedLocationFromEvent();
  }, [open, activeEvent?.id, activeEvent?.location?.latitude, activeEvent?.location?.longitude]);

  const reset = () => {
    setTitle('');
    setCategory(categoryOptions[0] || 'other');
    setSeverity(severityOptions[0] || 'medium');
    setAddress('');
    setLatitude(0);
    setLongitude(0);
    setError(null);
  };

  const handleCreate = async () => {
    setError(null);
    if (!title.trim() || !address.trim()) {
      setError('Title and location are required.');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/incidents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          eventId: activeEventId || '',
          title,
          description: '',
          category,
          severity,
          location: { latitude, longitude, address },
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        setError(`Server error (${res.status}): ${text || 'Unable to create incident'}`);
        return;
      }
      const data = await res.json();
      onCreated?.(data.incident);
      reset();
      onClose();
    } catch (e: any) {
      setError(`Network error: ${e?.message || 'Unable to reach server'}`);
    } finally {
      setCreating(false);
    }
  };

  const handleMapSelect = (lat: number, lng: number, addr: string) => {
    setLatitude(lat);
    setLongitude(lng);
    setAddress(addr);
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="w-full max-w-lg bg-card border border-border rounded-xl p-5 shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-foreground flex items-center gap-2">
              <Plus className="w-4 h-4 text-primary" />
              Create New Incident
            </h2>
            <button onClick={onClose} className="p-1 hover:bg-accent rounded-lg transition-colors">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          {activeEvent && (
            <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/15 space-y-1">
              <p className="text-xs font-semibold text-foreground">{activeEvent.name}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {activeEvent.eventDate}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {activeEvent.eventStartTime}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {formatEventLocationLabel(activeEvent)}
                </span>
              </div>
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }} className="space-y-3">
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Title *</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="e.g. Structure Fire - Main Street"
                required
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-foreground block mb-1">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as IncidentCategory)}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {categoryOptions.map((c) => (
                    <option key={c} value={c}>{getCategoryIcon(c)} {c.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-foreground block mb-1">Severity</label>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {severityOptions.map((s) => (
                    <option key={s} value={s}>{s.toUpperCase()}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Location / Address *</label>
              <div className="flex gap-2">
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Address or coordinates"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowMapPicker(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary/30 bg-primary/5 text-primary text-xs font-semibold hover:bg-primary/10 transition-colors flex-shrink-0"
                  title="Select location on map"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  Map
                </button>
              </div>
              {isValidGeoLocation({ latitude, longitude, address }) && (
                <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                  Coords: {latitude.toFixed(6)}, {longitude.toFixed(6)}
                </p>
              )}
            </div>
            {error && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-destructive/10 border border-destructive/20">
                <AlertTriangle className="w-3.5 h-3.5 text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-xs text-destructive">{error}</p>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !title.trim() || !address.trim()}
                className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {creating ? 'Creating...' : 'Create Incident'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Map Location Picker — mount only when opened to avoid unnecessary map init */}
      {showMapPicker && (
        <MapLocationPicker
          open={showMapPicker}
          onClose={() => setShowMapPicker(false)}
          onSelect={handleMapSelect}
          initialLocation={
            isValidGeoLocation({ latitude, longitude, address })
              ? { latitude, longitude, address }
              : activeEvent?.location
          }
        />
      )}
    </>
  );
}
